import { SeloDaCampanha } from '../cptu/CampanhaMoldura';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  alterarCamadaSpt, alterarFuroSpt, cancelarFuroSpt, apagarMetroSpt, excluirCamadaSpt, criarFuroSpt, dividirCamadaSpt, gravarAvancoSpt,
  gravarFenomenosSpt, gravarMetroSpt, gravarNaSpt, gravarRecomendacoesSpt, lerNumero, metro,
  unirCamadaSpt, verCampanhaSpt, verFuroSpt, vocabularioSpt,
  type CampanhaSpt as Campanha, type FuroSpt, type Vocabulario,
} from '../../api/spt';
import { useCaminho } from '../../Caminho';
import { hojeIso, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { abrirProtegido, Carregando, Dialogo, ErroDaApi } from '../../ui';
import { EdicaoRapidaDoSpt } from './EdicaoRapidaDoSpt';
import { PainelDaCamada } from './PainelDaCamada';
import { PerfilDoFuro } from './PerfilDoFuro';
import { JanelaDoMotivo, RelatorioSpt } from './RelatorioSpt';
import { RodapeDoFuro } from './RodapeDoFuro';
import { TabelaDeMetros } from './TabelaDeMetros';

/**
 * A campanha SPT e a ficha do furo — ondas 356/357 (spec 12 §5; RN-276).
 *
 * Em cima, a campanha (número `<OS>-SPT-NN`, OS, cliente, obra) e os furos em
 * fichas; no meio, o cabeçalho do furo; depois a tabela dos golpes à
 * esquerda, alinhada metro a metro com o perfil à direita (camadas criadas
 * no «+», limites e NA arrastados); embaixo, o rodapé (avanço, NA,
 * paralisação, fenômenos, recomendações, pendências). Tudo grava ao sair do
 * campo — a transcrição do boletim de papel é rápida e não tem «Salvar».
 */

const ALTURA_DA_LINHA = 30;

export function CampanhaSpt() {
  const { id, fid } = useParams();
  const navegar = useNavigate();
  const caminho = useLocation().pathname;
  const noRelatorio = caminho.endsWith('/relatorio');
  /* 366: a Edição rápida dos furos e a RN da OS. */
  const naEdicao = caminho.endsWith('/edicao');
  const { pode } = useSessao();
  const [c, setC] = useState<Campanha | null>(null);
  const [vocab, setVocab] = useState<Vocabulario | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [novo, setNovo] = useState(false);

  const recarregar = useCallback(async () => {
    try { setC(await verCampanhaSpt(id!)); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void recarregar(); vocabularioSpt().then(setVocab).catch(setErro); }, [recarregar]);
  useEffect(() => {
    if (c && !fid && !noRelatorio && !naEdicao && c.furos.length) navegar(`/spt/${c.id}/furos/${c.furos[0].id}`, { replace: true });
  }, [c, fid, navegar, noRelatorio, naEdicao]);
  /* A trilha (P-UI-23): «Sondagens SPT › Campanha <número> › Furo SP-01 | Relatório». */
  const furoAberto = c && fid ? c.furos.find((f) => f.id === fid) ?? null : null;
  useCaminho(c ? [
    { rotulo: 'Campanha', codigo: c.numero, para: noRelatorio || naEdicao ? `/spt/${c.id}` : undefined },
    ...(noRelatorio ? [{ rotulo: 'Relatório' }] : naEdicao ? [{ rotulo: 'Edição rápida' }] : furoAberto ? [{ rotulo: 'Furo', codigo: furoAberto.codigo }] : []),
  ] : null);

  if (erro && !c) return <ErroDaApi erro={erro} />;
  if (!c || !vocab) return <Carregando texto="Carregando a campanha SPT" />;

  return (
    <div className="spt-tela">
      <div className="cabeca">
        <div className="linha-topo">
          <h1 className="cresce">Sondagem SPT · <span className="mono">{c.numero}</span></h1>
          <SeloDaCampanha situacao={c.situacao} />
        </div>
        <p className="sub">
          {c.titulo} · OS <Link to={`/os/${c.os_id}/campo`} className="mono">{c.os_numero}</Link>
          {c.cliente && ` · ${c.cliente}`}{c.obra && ` · ${c.obra}`}
        </p>
      </div>

      <nav className="abas-os" aria-label="Partes da campanha">
        <Link to={c.furos.length ? `/spt/${c.id}/furos/${fid ?? c.furos[0].id}` : `/spt/${c.id}`}
          className={`aba-os${!noRelatorio && !naEdicao ? ' sel' : ''}`} aria-current={!noRelatorio && !naEdicao ? 'page' : undefined}>Furos</Link>
        <Link to={`/spt/${c.id}/edicao`}
          className={`aba-os${naEdicao ? ' sel' : ''}`} aria-current={naEdicao ? 'page' : undefined}>Edição rápida</Link>
        <Link to={`/spt/${c.id}/relatorio`}
          className={`aba-os${noRelatorio ? ' sel' : ''}`} aria-current={noRelatorio ? 'page' : undefined}>Relatório</Link>
      </nav>

      {noRelatorio ? <RelatorioSpt campanhaId={c.id} aoMudar={() => void recarregar()} />
        : naEdicao ? <EdicaoRapidaDoSpt c={c} podeEditar={pode('spt.editar')} aoGravado={recarregar} /> : <>
      <nav className="spt-furos" aria-label="Furos da campanha">
        {c.furos.map((f) => (
          <Link key={f.id} to={`/spt/${c.id}/furos/${f.id}`} aria-current={f.id === fid ? 'page' : undefined}
            className="chip spt-furo-chip">
            <b className="mono">{f.codigo}</b>
            <span>{f.profundidade_final == null ? '—' : `${metro(f.profundidade_final)} m`}</span>
            {f.pendencias > 0
              ? <span title="pendências">▲ {f.pendencias}<span className="so-leitor"> {plural(f.pendencias, 'pendência', 'pendências')}</span></span>
              : <span title="sem pendências">●<span className="so-leitor"> sem pendências</span></span>}
          </Link>
        ))}
        {c.editavel && <button type="button" className="botao secundario" onClick={() => setNovo(true)}>＋ Furo</button>}
      </nav>

      {c.furos.length === 0 && (
        <p className="sub">Nenhum furo ainda. Crie o primeiro com «＋ Furo» — ex.: SP-01, como está no boletim de campo.</p>
      )}
      {fid && <FichaDoFuro key={fid} furoId={fid} vocab={vocab} aoMudar={recarregar}
        aoRetirar={() => { void recarregar(); navegar(`/spt/${c.id}`, { replace: true }); }} />}
      </>}

      {novo && (
        <JanelaDeNovoFuro campanhaId={c.id} campanha={c.numero} sugestao={`SP-${String(c.furos.length + 1).padStart(2, '0')}`}
          aoFechar={() => setNovo(false)}
          aoCriar={(f) => { setNovo(false); void recarregar(); navegar(`/spt/${c.id}/furos/${f.id}`); }} />
      )}
    </div>
  );
}

function JanelaDeNovoFuro({ campanhaId, campanha, sugestao, aoFechar, aoCriar }: {
  campanhaId: string; campanha: string; sugestao: string; aoFechar(): void; aoCriar(f: FuroSpt): void;
}) {
  const [codigo, setCodigo] = useState(sugestao);
  const [erro, setErro] = useState<unknown>(null);
  const criar = async () => {
    try { aoCriar(await criarFuroSpt(campanhaId, codigo.trim())); } catch (e) { setErro(e); }
  };
  return (
    <Dialogo aberto titulo="Novo furo SPT" rotulo={`Campanha ${campanha}`} aoFechar={aoFechar}>
      <form onSubmit={(e) => { e.preventDefault(); void criar(); }}>
        <ErroDaApi erro={erro} />
        <label className="spt-rotulo">Código do furo
          <input className="campo" value={codigo} maxLength={20} autoFocus onChange={(e) => setCodigo(e.target.value)} />
        </label>
        <p className="sub">O furo vira um ponto da OS (tipo SPT). Se o ponto já existe, ele é aproveitado.</p>
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!codigo.trim()}>Criar o furo</button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </form>
    </Dialogo>
  );
}

function FichaDoFuro({ furoId, vocab, aoMudar, aoRetirar }: { furoId: string; vocab: Vocabulario; aoMudar(): Promise<void>; aoRetirar(): void }) {
  const { pode } = useSessao();
  const [retirar, setRetirar] = useState(false);
  const [f, setF] = useState<FuroSpt | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [excluir, setExcluir] = useState<string | null>(null);
  /* 361: a largura da coluna do perfil — a descrição cresce ou encolhe para a ficha caber na página, sem rolar para o lado. */
  const colPerfil = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(900);
  useEffect(() => {
    const el = colPerfil.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const o = new ResizeObserver(([e]) => setLargura(Math.floor(e.contentRect.width)));
    o.observe(el);
    return () => o.disconnect();
  }, [f != null]);

  useEffect(() => { verFuroSpt(furoId).then(setF).catch(setErro); }, [furoId]);

  /** Toda gravação devolve o furo inteiro; a lista dos furos (pendências) é refeita depois. */
  const aplicar = async (p: Promise<FuroSpt>) => {
    try { setF(await p); setErro(null); void aoMudar(); } catch (e) { setErro(e); }
  };
  if (!f) return erro ? <ErroDaApi erro={erro} /> : <Carregando texto="Carregando o furo" />;
  const ed = f.editavel && pode('spt.editar');
  const furo = { ...f, editavel: ed };
  const camada = f.camadas.find((k) => k.id === sel) ?? null;
  const naNoBoletim = f.na.find((l) => l.no_boletim && !l.seco)?.profundidade ?? null;
  const cab = (corpo: Record<string, unknown>) => void aplicar(alterarFuroSpt(f.id, corpo));
  const ultimo = f.metros.length ? f.metros[f.metros.length - 1].profundidade : null;

  return (
    <section className="spt-ficha" aria-label={`Furo ${f.codigo}`}>
      <ErroDaApi erro={erro} />
      <div className="cartao spt-cabecalho">
        <h2 className="mono">{f.codigo}</h2>
        {ed && (
          <button type="button" className="botao discreto" onClick={() => setRetirar(true)}>Retirar este furo</button>
        )}
        {retirar && (
          <JanelaDoMotivo titulo={`Retirar o furo ${f.codigo} da campanha`} aoFechar={() => setRetirar(false)}
            aoConfirmar={(m) => { setRetirar(false); cancelarFuroSpt(f.id, m).then(aoRetirar).catch(setErro); }} />
        )}
        <CampoDoCabecalho rotulo="Início" tipo="date" valor={f.inicio ?? ''} ed={ed} aoGravar={(v) => cab({ inicio: v || null })} />
        <CampoDoCabecalho rotulo="Hora de início" tipo="time" valor={f.hora_inicio ?? ''} ed={ed} aoGravar={(v) => cab({ horaInicio: v || null })} />
        <CampoDoCabecalho rotulo="Término" tipo="date" valor={f.termino ?? ''} ed={ed} aoGravar={(v) => cab({ termino: v || null })} />
        <CampoDoCabecalho rotulo="Hora de término" tipo="time" valor={f.hora_termino ?? ''} ed={ed} aoGravar={(v) => cab({ horaTermino: v || null })} />
        <CampoDoCabecalho rotulo="Sondador" valor={f.sondador ?? ''} ed={ed} aoGravar={(v) => cab({ sondador: v || null })} />
        <label className="spt-rotulo">Sistema
          <select className="campo" disabled={!ed} value={f.sistema} onChange={(e) => cab({ sistema: e.target.value })}>
            <option value="manual">Manual</option><option value="mecanizado">Mecanizado</option>
          </select>
        </label>
        <CampoDoCabecalho rotulo="Cota (m)" valor={metro(f.cota, 3)} ed={ed} aoGravar={(v) => cab({ cota: lerNumero(v) })} />
        <CampoDoCabecalho rotulo="UTM E (m)" valor={metro(f.utm_e, 2)} ed={ed} aoGravar={(v) => cab({ utmE: lerNumero(v), utmN: f.utm_n, utmFuso: f.utm_fuso ?? 22 })} />
        <CampoDoCabecalho rotulo="UTM N (m)" valor={metro(f.utm_n, 2)} ed={ed} aoGravar={(v) => cab({ utmE: f.utm_e, utmN: lerNumero(v), utmFuso: f.utm_fuso ?? 22 })} />
        <CampoDoCabecalho rotulo="Fuso" valor={f.utm_fuso == null ? '' : String(f.utm_fuso)} ed={ed} aoGravar={(v) => cab({ utmE: f.utm_e, utmN: f.utm_n, utmFuso: lerNumero(v) })} />
        <CampoDoCabecalho rotulo="Revestimento Ø (mm)" valor={f.revestimento_diametro == null ? '' : metro(f.revestimento_diametro, 1)} ed={ed}
          aoGravar={(v) => cab({ revestimentoDiametro: lerNumero(v) })} />
        <CampoDoCabecalho rotulo="Profundidade final (m)" valor={metro(f.profundidade_final)} ed={ed} aoGravar={(v) => cab({ profundidadeFinal: lerNumero(v) })} />
      </div>

      <div className="spt-meio">
        <div className="spt-col-tabela">
          <TabelaDeMetros metros={f.metros} fim={f.profundidade_final} editavel={ed} alturaDaLinha={ALTURA_DA_LINHA}
            aoGravar={(prof, golpes, penet) => aplicar(gravarMetroSpt(f.id, prof, { golpes, penet }))}
            aoMarcarNr={(m, nr) => void aplicar(gravarMetroSpt(f.id, m.profundidade, {
              golpes: [m.golpes_1, m.golpes_2, m.golpes_3], penet: [m.penet_1, m.penet_2, m.penet_3], amostra: m.amostra,
              semRecuperacao: nr,
            }))} />
          <button type="button" className="botao discreto"
            onClick={() => { abrirProtegido(`/spt/furos/${f.id}/boletim.pdf`, `boletim-${f.codigo}-PRELIMINAR.pdf`).catch(setErro); }}>
            Boletim em PDF (prévia)
          </button>
          {ed && ultimo != null && (
            <button type="button" className="botao discreto" onClick={() => void aplicar(apagarMetroSpt(f.id, ultimo))}>
              Excluir o metro {metro(ultimo)}
            </button>
          )}
        </div>
        <div className="spt-col-perfil" ref={colPerfil}>
          {f.profundidade_final == null
            ? <p className="sub">Informe a profundidade final para desenhar o perfil e criar as camadas.</p>
            : (
              <PerfilDoFuro metros={f.metros} camadas={f.camadas} avanco={f.avanco} fenomenos={f.fenomenos}
                fim={f.profundidade_final} na={naNoBoletim} rh={ALTURA_DA_LINHA} selecionada={sel} editavel={ed}
                largura={largura} geneses={vocab.geneses}
                aoEscolher={setSel}
                aoMudarGenese={(cid, g) => void aplicar(alterarCamadaSpt(cid, { genese: g }))}
                aoExcluir={(cid) => {
                  /* A primeira camada não pergunta: junta-se à de baixo (pedido do Humberto, 30/10/2026). */
                  if (f.camadas[0]?.id === cid) {
                    if (sel === cid) setSel(null);
                    void aplicar(excluirCamadaSpt(cid, 'baixo'));
                  } else setExcluir(cid);
                }}
                aoDividir={(p) => void aplicar(dividirCamadaSpt(f.id, p))}
                aoMudarBase={(cid, base) => void aplicar(alterarCamadaSpt(cid, { base }))}
                aoMudarFim={(fim) => cab({ profundidadeFinal: fim })}
                aoMudarNa={(p) => {
                  const lista = f.na.length ? f.na : [{ data: f.termino ?? f.inicio ?? hojeIso(), hora: null, seco: false, profundidade: p, no_boletim: true }];
                  void aplicar(gravarNaSpt(f.id, lista.map((l) => ({
                    data: l.data, hora: l.hora, seco: l.no_boletim ? false : l.seco,
                    profundidade: l.no_boletim ? p : l.profundidade, noBoletim: l.no_boletim,
                  }))));
                }} />
            )}
        </div>
        <PainelDaCamada camada={camada} numero={camada ? f.camadas.indexOf(camada) + 1 : 0} vocab={vocab} fenomenos={f.fenomenos}
          podeUnir={!!camada && f.camadas.indexOf(camada) < f.camadas.length - 1} editavel={ed}
          aoMudar={(corpo) => { if (camada) void aplicar(alterarCamadaSpt(camada.id, corpo)); }}
          aoUnir={() => { if (camada) void aplicar(unirCamadaSpt(camada.id)); }}
          aoFechar={() => setSel(null)} />
      </div>
      {excluir && (() => {
        const i = f.camadas.findIndex((k) => k.id === excluir);
        if (i < 0) return null;
        const c = f.camadas[i];
        const cima = f.camadas[i - 1];
        const baixo = f.camadas[i + 1];
        const fazer = (j: 'cima' | 'baixo') => {
          setExcluir(null);
          if (sel === c.id) setSel(null);
          void aplicar(excluirCamadaSpt(c.id, j));
        };
        const desc = (k: typeof c) => k.descricao || 'sem material';
        return (
          <Dialogo aberto titulo={`Excluir a camada de ${metro(c.topo)} a ${metro(c.base)} m`} rotulo="Camada" aoFechar={() => setExcluir(null)}>
            <>
              <p>O intervalo dela passa para a camada vizinha, que fica com a própria descrição.</p>
              {!baixo && <p className="sub">É a última camada: o furo vai até {metro(f.profundidade_final)} m, então a camada de cima desce até o fim.</p>}
              <div className="rodape-dialogo">
                <button type="button" className="botao" onClick={() => fazer('cima')}>
                  {baixo ? 'Juntar à camada de cima' : 'Excluir (a de cima desce até o fim)'}
                </button>
                {baixo && <button type="button" className="botao secundario" onClick={() => fazer('baixo')}>Juntar à camada de baixo</button>}
                <button type="button" className="botao discreto" onClick={() => setExcluir(null)}>Cancelar</button>
              </div>
              <p className="sub">De cima: {desc(cima)}.{baixo ? ` De baixo: ${desc(baixo)}.` : ''}</p>
            </>
          </Dialogo>
        );
      })()}

      <RodapeDoFuro furo={furo} vocab={vocab} gravar={{
        furo: (c) => aplicar(alterarFuroSpt(f.id, c)),
        avanco: (a) => aplicar(gravarAvancoSpt(f.id, a as never)),
        na: (n) => aplicar(gravarNaSpt(f.id, n)),
        fenomenos: (x) => aplicar(gravarFenomenosSpt(f.id, x)),
        recomendacoes: (r) => aplicar(gravarRecomendacoesSpt(f.id, r)),
      }} />
    </section>
  );
}

function CampoDoCabecalho({ rotulo, valor, tipo = 'text', ed, aoGravar }: {
  rotulo: string; valor: string; tipo?: string; ed: boolean; aoGravar(v: string): void;
}) {
  const [v, setV] = useState(valor);
  useEffect(() => setV(valor), [valor]);
  return (
    <label className="spt-rotulo">{rotulo}
      <input className="campo" type={tipo} value={v} disabled={!ed} onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v !== valor) aoGravar(v.trim()); }} />
    </label>
  );
}
